import addonsApi, { useGetAddonsDataQuery } from '@/src/api/rtkQuery/addonsApi'
import { useGetItineraryDataQuery } from '@/src/api/rtkQuery/itineraryApi'
import useFormatCurrencyForItinerary from '@/src/hooks/currency/useFormatCurrencyForItinerary'
import { useAppDispatch } from '@/src/redux/store'
import { DeckType, SeatDetail, SelectedAncillaryType } from '@/src/types/itinerary'
import { useCallback, useState } from 'react'
import useGetItineraryId from '../../useGetItineraryId'

const useSeatProcessor = () => {
    const itineraryId = useGetItineraryId()
    const { formatCurrency } = useFormatCurrencyForItinerary()
    const { data: addonsData } = useGetAddonsDataQuery({ itineraryId })
    const dispatch = useAppDispatch()

    const [selectedSegmentIndex, setSelectedSegmentIndex] = useState<number>(0)
    const [selectedDeck, setSelectedDeck] = useState<DeckType>('lowerDeck')

    const { paxCount } = useGetItineraryDataQuery(
        { itineraryId },
        {
            selectFromResult: ({ data }) => {
                try {
                    if (data) {
                        return {
                            paxCount: data.searchCriteria.paxInfo.adults + data.searchCriteria.paxInfo.children
                        }
                    }
                    return {
                        paxCount: 0
                    }
                } catch (e) {
                    return {
                        paxCount: 0
                    }
                }
            }
        }
    )

    const { journeyAncillaries, selectedAncillaries = [] } = addonsData || {}
    const { seatAncillaries } = journeyAncillaries || {}
    const { availableSegments = [], segmentWiseAncillaryData } = seatAncillaries || {}

    const selectedSegment = availableSegments?.[selectedSegmentIndex] || { from: '', to: '', key: '' }
    const seatOptions = segmentWiseAncillaryData?.[selectedSegment.key]
    const seatData = seatOptions?.[selectedDeck]
    const selectedSeatOptions =
        selectedAncillaries.find(
            ancillary =>
                ancillary.segmentOriginDestinationInfo.from === selectedSegment.from &&
                ancillary.segmentOriginDestinationInfo.to === selectedSegment.to
        )?.seatDetails || []

    const handleAddSeat = useCallback(
        (seat: SeatDetail) => {
            dispatch(
                addonsApi.util.updateQueryData('getAddonsData', { itineraryId }, draft => {
                    if (!draft.selectedAncillaries) {
                        draft.selectedAncillaries = []
                    }

                    const selectedAncillarySegmentIndex = draft.selectedAncillaries.findIndex(
                        ancillary =>
                            ancillary.segmentOriginDestinationInfo.from === selectedSegment.from &&
                            ancillary.segmentOriginDestinationInfo.to === selectedSegment.to
                    )

                    if (selectedAncillarySegmentIndex !== -1) {
                        // Update existing segment
                        // Check if adding this seat would exceed paxCount, remove first seat if needed (FIFO)
                        if (draft.selectedAncillaries[selectedAncillarySegmentIndex].seatDetails.length >= paxCount) {
                            draft.selectedAncillaries[selectedAncillarySegmentIndex].seatDetails.shift()
                        }
                        draft.selectedAncillaries[selectedAncillarySegmentIndex].seatDetails.push(seat)
                    } else {
                        // Add new segment
                        draft.selectedAncillaries.push({
                            segmentOriginDestinationInfo: {
                                from: selectedSegment.from,
                                to: selectedSegment.to
                            },
                            mealDetails: [],
                            baggageDetails: [],
                            seatDetails: [seat]
                        } as SelectedAncillaryType)
                    }
                })
            )
        },
        [itineraryId, selectedSegment, paxCount]
    )

    const handleRemoveSeat = useCallback(
        (seat: SeatDetail) => {
            dispatch(
                addonsApi.util.updateQueryData('getAddonsData', { itineraryId }, draft => {
                    if (!draft.selectedAncillaries) return

                    const selectedAncillarySegmentIndex = draft.selectedAncillaries.findIndex(
                        ancillary =>
                            ancillary.segmentOriginDestinationInfo.from === selectedSegment.from &&
                            ancillary.segmentOriginDestinationInfo.to === selectedSegment.to
                    )

                    if (selectedAncillarySegmentIndex !== -1) {
                        // Find the first occurrence of the meal to remove
                        const seatIndex = draft.selectedAncillaries[
                            selectedAncillarySegmentIndex
                        ].seatDetails.findIndex(seatDetail => seatDetail.seatNumber === seat.seatNumber)

                        // Remove only the first occurrence if found
                        if (seatIndex !== -1) {
                            draft.selectedAncillaries[selectedAncillarySegmentIndex].seatDetails.splice(seatIndex, 1)
                        }
                    }
                })
            )
        },
        [itineraryId, selectedSegment]
    )

    return {
        availableSegments,
        selectedSegmentIndex,
        seatData,
        selectedSeatOptions,
        setSelectedSegmentIndex,
        handleAddSeat,
        handleRemoveSeat,
        formatCurrency
    }
}

export default useSeatProcessor
